/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, from, switchMap, take, throwError } from 'rxjs';
import { AppConfig } from '../../app.config';
import { ApiConfig } from '../api-config/api-config';
import { AUTHORIZATION, CALL_CENTER_TOKEN, updateHeaders } from '../model';
import { ApiSelectorService } from '../api-selector/api-selector.service';
import { AuthService } from '../authentication/auth.service';
import { ApiError } from '../model/utility';

export const AS_IS = 'as-is';

/**
 * Updates API request headers with auth token as needed/appropriate
 */
@Injectable()
export class HttpInterceptorService implements HttpInterceptor {

  constructor(
    private readonly apiService: ApiSelectorService,
    private readonly authService: AuthService,
  ) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    req = this.updateRequestHeaders(req);
    req = this.apiService.updateRequest(req);
    return next.handle(req).pipe(catchError((error) => {
      if (this.isUnauthorizedApiError(error, req.url)) {
        return this.handleUnauthorized(error, req, next);
      } else {
        return throwError(() => error);
      }
    }));
  }

  private isApiHost(url: string): boolean {
    let isApi = false;
    isApi = isApi || Object.keys(AppConfig.hosts).findIndex((key) => url.startsWith(AppConfig.hosts[key])) !== -1;
    isApi = isApi || this.apiService.containsValueForUrl(url);
    return isApi;
  }

  private isUnauthorizedApiError(error: ApiError, url: string): boolean {
    return error.status === 401 && this.isApiHost(url);
  }

  private updateRequestHeaders(req: HttpRequest<unknown>): HttpRequest<unknown> {
    let headers = req.headers;
    if (headers.has(AS_IS)) return req;
    if (!this.isApiHost(req.url)) return req;
    const requiredHeaders = ApiConfig.apiHeaders;
    requiredHeaders.keys().forEach((key) => {
      headers = updateHeaders(headers, key, requiredHeaders.get(key));
    });

    // ROAD-12747, ROAD-12750
    if (this.isApiHost(req.url)) {
      const clientHeaders = ApiConfig.clientHeaders;
      clientHeaders.keys().forEach((key) => {
        headers = updateHeaders(headers, key, clientHeaders.get(key));
      });
    }

    const callCenterToken = req.headers.get(CALL_CENTER_TOKEN);
    if (callCenterToken) {
      headers = updateHeaders(headers, AUTHORIZATION, 'Bearer ' + callCenterToken);
      headers = updateHeaders(headers, CALL_CENTER_TOKEN, null);
    }

    return req.clone({ headers: headers });
  }

  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private handleUnauthorized(originalError: any, req: HttpRequest<unknown>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(this.authService.refreshAccessToken()).pipe(
        switchMap((token: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);
          req = this.updateRequestHeaders(req);
          return next.handle(req);
        }),
        catchError((error) => {
          this.authService.showForcedLogout(error);
          return throwError(() => originalError);
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((_token) => {
          req = this.updateRequestHeaders(req);
          return next.handle(req);
        })
      );
    }
  }
}
